import React from 'react';
import {Icons} from '../../constants/icons';
import {App} from '../../app/AppSingleton';
import {Board, Column, EntityID, Task} from '../../models/types';
import './TaskEditorCore.css';
import {BackButtonHandler} from "../../utils/BackButtonHandler";

export interface TaskEditorCoreProps {
	// Основные пропсы
	mode: 'add' | 'edit';
	initialTitle: string;

	taskId?: EntityID;
	columnId?: EntityID;
	boardId: EntityID;

	app?: App;
	onSubmit: (title: string) => Promise<void>;
	onCancel?: () => void;
	onDelete?: () => Promise<void>;

	/* When editor closed */
	onClose?: (isDeleted:boolean) => void;

	// Настройки
	autoFocus?: boolean;
	showCancelButton?: boolean;
	placeholder?: string;
	hintText?: string;

	// Стилизация
	className?: string;
	inputClassName?: string;
	actionsPosition?: 'header' | 'footer';
}

interface TaskEditorCoreState {
	title: string;
	isSaving: boolean;
	showActions: boolean;
	showMoveMenu: boolean;
}

export class TaskEditorCore extends React.Component<TaskEditorCoreProps, TaskEditorCoreState> {
	private classBlock = 'task-editor-core';
	private inputRef = React.createRef<HTMLTextAreaElement>();
	private actionsRef = React.createRef<HTMLDivElement>();
	private ref =  React.createRef<HTMLDivElement>();
	private backButtonUnregister: (() => void) | null = null;


	constructor(props: TaskEditorCoreProps) {
		super(props);
		this.state = {
			title: props.initialTitle,
			isSaving: false,
			showActions: false,
			showMoveMenu: true,
		};
	}

	// ==================== LIFECYCLE METHODS ====================

	componentDidMount(): void {
		const current = this.inputRef.current;
		if (this.props.autoFocus && current) {
			current?.focus();
			//current?.select();
		}
		this.checkCurrentColumn();

		document.addEventListener('mousedown', this.handleClickOutside);
		setTimeout(() => this.updateTextareaHeight(), 0); // На следующий тик, после рендера

		this.backButtonUnregister = BackButtonHandler.getInstance().register(()=>{
			// Всегда обрабатываем нажатие кнопки "назад", чтобы предотвратить закрытие приложения
			if (this.props.onCancel) {
				this.handleCancelEdit();
			} else {
				// Если onCancel не определен, просто закрываем редактор
				this.close(false);
			}
			return true; // Всегда возвращаем true, чтобы предотвратить стандартное поведение
		})
	}

	componentWillUnmount(): void {
		document.removeEventListener('mousedown', this.handleClickOutside);

		// Отписываемся от обработчика кнопки "назад"
		if (this.backButtonUnregister) {
			this.backButtonUnregister();
			this.backButtonUnregister = null;
		}
	}

	// ==================== ARROW METHODS (коллбэки) ====================

	private handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
		this.setState({ title: e.target.value }, this.updateTextareaHeight);
	};

	private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
		if (e.key === 'Escape' && this.props.onCancel) {
			e.preventDefault();
			this.props.onCancel();
		} else if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			this.handleSubmit();
		}
	};
	private handleCancelEdit = ()=> {
		this.close(false, ()=>{
			this.props.onCancel?.()
		});
	}

	private handleSubmit = async (): Promise<void> => {
		const { title, isSaving } = this.state;
		const { onSubmit } = this.props;

		if (!title.trim() || isSaving) return;
		this.setState({ isSaving: true });
		try {
			await onSubmit(title.trim());
		} catch (error) {
			console.error('Error saving task:', error);
		} finally {
			this.setState({ isSaving: false });
			this.close(false);
		}
	};

	private handleDelete = async (): Promise<void> => {
		const { isSaving } = this.state;
		const { onDelete } = this.props;

		if (!onDelete || isSaving) return;

		try {
			await onDelete();
		} catch (error) {
			console.error('Error deleting task:', error);
		}
		finally {
			this.close(true);
		}
	};

	private handleMoveTask = async (targetColumnId: string): Promise<void> => {
		const { taskId, onCancel } = this.props;
		const { title, isSaving } = this.state;
		if (!taskId || isSaving || targetColumnId === this.props.columnId) {
			return;
		}
		this.setState({ isSaving: true });

		const app = App.getInstance();
		const runMoveTask = (result: any) => {
			// Перемещаем задачу в начало целевой колонки (newIndex = 0)
			return app.taskMove(taskId, targetColumnId, 0);
		};
		const updates:Partial<Task> = {
			id: taskId,
			title: title.trim(),
		};

		try {
			if (!app.getTaskEditController().isChanged(updates)) {
				runMoveTask({});
			} else {
				await app.getTaskEditController().updateTask(updates).then(runMoveTask)
			}

			// Закрываем редактор после успешного перемещения
			if (onCancel) {
				onCancel();
				this.close(false);
			}
		} catch (error) {
			console.error('Error moving task:', error);
		} finally {
			this.setState({ 
				isSaving: false,
				showActions: false,
				showMoveMenu: false 
			});
			this.close(false);
		}
	};

	private handleActionsToggle = (): void => {
		this.setState(prevState => ({ 
			showActions: !prevState.showActions,
			showMoveMenu: false 
		}));
	};

	private handleClickOutside = (event: MouseEvent): void => {
		const actionsElement = this.actionsRef.current;
		const isClickInside = actionsElement && actionsElement.contains(event.target as Node);
		if (!isClickInside) {
			this.setState({ showActions: false });
		}
	};

	// ==================== REGULAR METHODS (вспомогательные) ====================

	private updateTextareaHeight(): void {
		if (this.inputRef.current) {
			const input = this.inputRef.current;
			input.style.height = 'auto';
			input.style.height = `${input.scrollHeight}px`;
		}
	}

	private checkCurrentColumn(): void {
		// Проверяем, есть ли текущая колонка задачи
		const { columnId, app } = this.props;
		if (!columnId ) return;

		const board = this.getBoard();
		const columnExists = board.columns.some(col => col.id === columnId);
		if (!columnExists) {
			console.warn(`Column ${columnId} not found in current board`);
		}
	}

	private getBoard() :Board{
		const board = App.getInstance().getBoardById(this.props.boardId);
		if (!board) {
			throw Error(`Board '${this.props.boardId}' not found`);
		}
		return board;
	}

	private getInputClassName(): string {
		const { inputClassName } = this.props;
		const classElement = `${this.classBlock}__input`;
		const customClass = inputClassName || '';

		return `${classElement} ${customClass}`.trim();
	}

	private getSaveButtonClassName(): string {
		const { isSaving } = this.state;
		const classElement = `${this.classBlock}__save-button`;
		const classSaving = isSaving ? `${classElement}--saving` : '';

		return `${classElement} ${classSaving}`.trim();
	}
	private renderActions(): React.ReactNode {
		const { isSaving, showMoveMenu } = this.state;
		const { mode } = this.props;
		const classBlock = this.classBlock;
		const classActions = `${classBlock}__actions`;
		const classToggle = `${classBlock}__actions-toggle`;
		const classDropdown = `${classBlock}__actions-dropdown`;
		const classActionItem = `${classBlock}__action-item`;
		const classMoveItem = `${classActionItem}--move`;
		const classDelete = `${classActionItem}--delete`;
		const classDivider = `${classBlock}__divider`;

		//if (mode !== 'edit') return null;

		let divider = <div className={classDivider} />;
		return (
			<div className={classActions} ref={this.actionsRef}>
				<button
					className={classToggle}
					onClick={this.handleActionsToggle}
					aria-label="Действия"
					type="button"
					disabled={isSaving}
				>
					{Icons.Menu}
				</button>
				{this.state.showActions && (
					<div className={classDropdown}>
						<button
							className={`${classActionItem} ${classDelete}`}
							onClick={this.handleDelete}
							disabled={isSaving}
							type="button"
						>
							{Icons.Delete} Архивировать
						</button>
					</div>
				)}
			</div>
		);
	}
	private renderFloatingActions(): React.ReactNode {
		const { isSaving } = this.state;
		const { columnId } = this.props;

		const actionDelete = <>{/* Строка 2: Основные действия */}
			<div className="task-editor-core__actions-row">
				<button
					className="task-editor-core__delete-button"
					onClick={this.handleDelete}
					disabled={isSaving}
					type="button"
				>
					{Icons.Delete} Архивировать задачу
				</button>
			</div>
		</>;
		return (
			<div className="task-editor-core__floating-actions">
				{/* Строка 1: Все колонки */}
				<div className="task-editor-core__columns-row">
					{this.renderButtonsMoveTo()}
				</div>
			</div>
		);
	}
	private renderButtonsMoveTo(): React.ReactNode {
		const { isSaving } = this.state;
		const { columnId } = this.props;
		const board = this.getBoard();
		const columns = board.columns || [];

		const classElement = `task-editor-core__column-button`;
		return columns.map((column: Column) => {
			const isCurrent = column.id === columnId;
			const classCurrent = isCurrent ? `${classElement}--current` : '';
			const handleClick = isCurrent ? undefined : () => this.handleMoveTask(column.id);

			return (
				<button
					key={column.id}
					type="button"
					className={`${classElement} ${classCurrent}`}
					onClick={handleClick}
					disabled={isSaving || isCurrent}
					title={isCurrent ? 'Текущая колонка' : `Сохранить и переместить в "${column.title}"`}
				>
					<span className={`${classElement}-text`}>{column.title}</span>
					{isCurrent && <span className={`${this.classBlock}__current-indicator`}>{Icons.Checked}</span>}
				</button>
			);
		});
	}
	private renderMoveMenu(): React.ReactNode {
		const { isSaving, showMoveMenu } = this.state;
		const { columnId, app } = this.props;
		const classBlock = this.classBlock;
		const classSubmenu = `${classBlock}__submenu`;
		const classMoveItem = `${classBlock}__move-item`;
		const classCurrent = `${classMoveItem}--current`;
		const classDivider = `${classBlock}__divider`;

		if (!columnId) return null;

		const board = this.getBoard();
		const columns = board.columns || [];

		// Фильтруем колонки, исключая текущую
		const otherColumns = columns.filter((column) => column.id !== columnId);

		return (
			<React.Fragment>
				{otherColumns.length > 0 ? (
					otherColumns.map((column: Column) => {
						const handleClick = () => this.handleMoveTask(column.id);

						return (
							<button
								key={column.id}
								className={classMoveItem}
								onClick={handleClick}
								disabled={isSaving}
								type="button"
								title={`Переместить в "${column.title}"`}
							>
								<span>{column.title}</span>
							</button>
						);
					})
				) : (
					<div className={classMoveItem} style={{ opacity: 0.6, cursor: 'default' }}>
						Нет других колонок
					</div>
				)}

				{/* Показываем текущую колонку внизу (только для информации) */}
				{columns.length > 0 && (
					<>
						<div className={classDivider} />
						<div className={`${classMoveItem} ${classCurrent}`} style={{ opacity: 0.6 }}>
							<span>
							Текущая: {columns.find(c => c.id === columnId)?.title || 'Неизвестно'}
							</span>
							<span>✓</span>
						</div>
					</>
				)}
			</React.Fragment>
		);
	}

	private renderHeader(): React.ReactNode {
		const { mode, actionsPosition } = this.props;

		if (actionsPosition !== 'header' || mode !== 'edit') {
			return null;
		}

		const classElement = `${this.classBlock}__header`;
		return (
			<div className={classElement}>
				{this.renderActions()}
			</div>
		);
	}

	private renderContent(): React.ReactNode {
		const { title, isSaving } = this.state;
		const { placeholder } = this.props;
		const classElement = `${this.classBlock}__content`;

		return (
			<div className={classElement}>
				<textarea
					ref={this.inputRef}
					value={title}
					onChange={this.handleInputChange}
					onKeyDown={this.handleKeyDown}
					placeholder={placeholder}
					className={this.getInputClassName()}
					disabled={isSaving}
					rows={1}
				/>
			</div>
		);
	}

	private renderControls(): React.ReactNode {
		const { title, isSaving } = this.state;
		const { mode, onCancel, showCancelButton, hintText, actionsPosition } = this.props;

		const classBlock = this.classBlock;
		const classControls = `${classBlock}__controls`;
		const classButtons = `${classBlock}__buttons`;
		const classCancel = `${classBlock}__cancel-button`;
		const classHint = `${classBlock}__hint`;

		const classSavingIndicator = `${classBlock}__saving-indicator`;
		const classSpinner = `${classBlock}__spinner`;
		const classSavingText = `${classBlock}__saving-text`;

		return (
			<div className={classControls}>
				<div className={classButtons}>
					<button
						onClick={this.handleSubmit}
						disabled={isSaving || !title.trim()}
						className={this.getSaveButtonClassName()}
						aria-label="Сохранить задачу"
						type="button"
					>
						{isSaving ? (
							<div className={classSavingIndicator}>
								<div className={classSpinner}></div>
								<span className={classSavingText}>Сохранение...</span>
							</div>
						) : (
							'Сохранить'
						)}
					</button>

					{showCancelButton && onCancel && (
						<button
							onClick={this.handleCancelEdit}
							disabled={isSaving}
							className={classCancel}
							aria-label="Отменить"
							type="button"
						>
							Отмена
						</button>
					)}
					{actionsPosition === 'footer' && mode === 'edit' && this.renderActions()}

				</div>

				{hintText && (
					<div className={classHint}>
						{hintText}
					</div>
				)}
			</div>
		);
	}

	// ==================== RENDER METHOD ====================

	render(): React.ReactNode {
		const { mode, className } = this.props;
		const baseClass = this.classBlock;
		const customClass = className || '';
		const dataMode = `data-mode="${mode}"`;

		return (
			<div ref={this.ref}
				className={`${baseClass} ${customClass}`.trim()}
				{...{ [dataMode.split('=')[0]]: mode }}
			>
				{/* Основной редактор */}
				<div className="task-editor-core__main">
					{this.renderHeader()}
					{this.renderContent()}
					{this.renderControls()}
				</div>

				{/* Плавающие действия (только в режиме edit) */}
				{mode === 'edit' && this.renderFloatingActions()}
			</div>
		);
	}

	// noinspection JSMethodCanBeStatic
	private getAnimationSpeed(element:HTMLElement|null) {
		if (!element){
			return 200;
		}
		const speedValue = getComputedStyle(element)
			.getPropertyValue('--editor-animation-speed')
			.trim();

		// Проверяем единицы измерения
		if (speedValue.endsWith('ms')) {
			return parseFloat(speedValue); // уже в миллисекундах
		} else if (speedValue.endsWith('s')) {
			return parseFloat(speedValue) * 1000; // конвертируем в мс
		} else {
			// Без единиц - предполагаем секунды
			return parseFloat(speedValue || '0.3') * 1000;
		}
	}

	/** Run animation and wait , after that run close action.
	 *
	 * @private
	 */
	private close(isDeleted:boolean, afterAction: (() => void) | null = null) {
		this.ref.current?.classList.add("task-editor-core--exiting");
		setTimeout(()=>{
			this.props.onClose?.(isDeleted);
			afterAction?.();
		}, this.getAnimationSpeed(this.ref.current));
	}
}